import React, { PropsWithChildren } from 'react'
import { Icon } from '@chakra-ui/icons'
import { ButtonProps } from '@chakra-ui/react'
import { FaPaperPlane } from 'react-icons/fa6'

import SharedButton from '~components/shared/buttons/shared-button'
import { useChatForClient } from '~components/chats/use-chat-for-client'
import { useOpenChats } from '~components/whiteboard/open-chats-provider'
import { useCreateChatMutation } from '~graphql/generated/graphql'
import { SimpleClient, SimplePatient } from '~utils/shared-types'

interface Props {
    client: SimpleClient
    /** Optional patient context to pre-select in the chat. */
    patient?: SimplePatient
    /** Pre-fills the input draft (only when no existing draft exists). */
    defaultText?: string
    /** Override clinic for chat lookup (e.g. enterprise contexts). */
    clinicId?: string
    /**
     * If provided, fires `suggestChatResponse` with this prompt on open and
     * seeds the input with the AI-generated reply.
     */
    aiPrompt?: string
    size?: ButtonProps['size']
}

/**
 * Compact "open this client's chat in a popover" button. Drop-in replacement
 * for the legacy `QuickMessageButton`. Uses the shared chat popover system,
 * so the popover persists across pages, supports multiple windows, and shares
 * UX with the inbox / whiteboard.
 *
 * Creates a chat if the client does not have one yet, then opens the
 * shared pop-out window.
 */
export default function OpenChatButton({
    client,
    patient,
    defaultText,
    clinicId,
    aiPrompt,
    size = 'xs',
    children,
}: PropsWithChildren<Props>) {
    const { chat, clinicId: resolvedClinicId, loading } = useChatForClient({
        client,
        clinicId,
    })
    const { openChat } = useOpenChats()
    const [createChat, { loading: creatingChat }] = useCreateChatMutation()

    async function handleClick() {
        let chatId = chat?.id

        if (!chatId && resolvedClinicId) {
            const { data } = await createChat({
                variables: {
                    data: {
                        clinic: resolvedClinicId,
                        client: client.id,
                    },
                },
            })
            chatId = data?.createChat.id
        }

        if (!chatId) return

        openChat(chatId, {
            aiPrompt,
            defaultText,
            patientId: patient?.id,
        })
    }

    return (
        <SharedButton
            size={size}
            variant="outline"
            leftIcon={<Icon as={FaPaperPlane} />}
            onClick={handleClick}
            isLoading={creatingChat}
            isDisabled={loading || !resolvedClinicId}
        >
            {children || 'Send a quick message'}
        </SharedButton>
    )
}
